/**
 * Cent-accurate money arithmetic for XanBZS ERP.
 *
 * Currency is stored and exchanged as `numeric` dollars, so ordinary IEEE-754
 * sums drift below the cent (`4.35 + 5.65 === 9.999999999999998`). Comparing or
 * persisting those raw sums makes a fully paid document read as partially paid.
 * Normalize to whole cents before comparing or writing.
 *
 * These helpers are display-agnostic; `@/lib/format` owns rendering.
 */

const CENTS_PER_UNIT = 100;
const CENT_EXPONENT_SHIFT = 2;

/**
 * Decimal places kept before the cent tie is decided.
 *
 * Far below the drift a document-sized sum accumulates, and far above the three
 * decimals a unit price is entered with, so the snap discards float error
 * without discarding a digit anyone typed or the database stored.
 */
const DECIMAL_SNAP_PLACES = 10;

/** Largest cent count representable by the numeric(15,2) money columns. */
const MAX_PERSISTED_CENTS = 999_999_999_999_999;

/**
 * Bounds a shifted cent count without substituting a figure for a real one.
 *
 * Only the exponent shift overflowing to `Infinity` is coerced, and only to the
 * same `0` a non-finite input yields — clamping the merely-large would replace
 * amounts this module computes exactly (`1e14` cents is exact) with a plausible
 * wrong one, which is the failure mode {@link assertMoneyAmount} exists to
 * prevent. Amounts too large to settle are rejected there instead.
 */
function boundCents(shifted: number): number {
  const magnitude = Math.round(Math.abs(shifted));

  if (!Number.isFinite(magnitude) || magnitude === 0) {
    return 0;
  }
  return shifted < 0 ? -magnitude : magnitude;
}

/**
 * How far from a half cent the binary value must sit for it to decide the
 * rounding on its own, as a fraction of the value's own magnitude.
 *
 * Float error grows with magnitude, so a fixed absolute tolerance is wrong: at
 * `1.9e10` the drift already exceeds a micro-cent and a flat `1e-6` guard sends
 * genuinely tied values down the cheap path. `2^-40` sits far above the ~`2^-53`
 * relative error a product or document-sized sum accumulates, and far below the
 * distance a non-tie keeps from the boundary.
 */
const TIE_TOLERANCE_RATIO = 2 ** -40;

/** Smallest tie window, so tiny magnitudes still get a usable guard. */
const MIN_TIE_TOLERANCE = 1e-6;

const HALF = 0.5;

/**
 * Decides the cent on the exact decimal rather than on the binary value.
 *
 * Only reached for values sitting on a half-cent boundary, where the two
 * disagree — see {@link quantizeToCents}.
 */
function quantizeTieOnDecimal(amount: number): number {
  // Snaps binary drift for rounding math; this is not display formatting.
  // dv-disable-next-line no-adhoc-format
  const snapped = Number(amount.toFixed(DECIMAL_SNAP_PLACES));
  const [mantissa, exponent] = snapped.toExponential().split("e");
  const shifted = Number(`${mantissa}e${Number(exponent) + CENT_EXPONENT_SHIFT}`);

  return boundCents(shifted);
}

/**
 * Rounds to whole cents on the decimal value, half away from zero.
 *
 * Currency columns are `numeric`, so the database rounds the *exact decimal*:
 * `round(0.435, 2)` is `0.44`. Two separate IEEE-754 artifacts break agreement
 * with that, and both have to be undone.
 *
 * The binary value is not the decimal. `Math.round(amount * 100)` reads a
 * decimal half-cent such as `1.035` (held as `1.03499…`) as `103.49999999999999`
 * and rounds down where Postgres and `Intl.NumberFormat` round up. Shifting the
 * exponent of the round-trip representation decides the tie on the decimal
 * instead, so quantizing, formatting, and the database agree on `1.04`.
 *
 * The round-trip representation is not the exact decimal either, once the
 * amount has been *computed*. `3 * 0.145` is `0.43499999999999994`, whose
 * shortest representation is still below the half cent — so the exponent shift
 * alone bills `0.43` against the `0.44` the database derives from the exact
 * `numeric` product. Snapping to a decimal grid first recovers the `0.435` that
 * product actually carries. Computed products and sums are what the finance
 * helpers pass in, so this is the path that matters.
 *
 * Away from a half cent the two agree, and only the tie needs deciding on the
 * decimal. Since `roundToCents` runs at every money boundary — including
 * per-row line totals and unmemoized subtotals that re-run on each keystroke —
 * the arithmetic answers the common case and the string path handles the tie.
 */
function quantizeToCents(amount: number): number {
  const scaled = amount * CENTS_PER_UNIT;
  const distanceFromTie = Math.abs(Math.abs(scaled % 1) - HALF);
  const tolerance = Math.max(MIN_TIE_TOLERANCE, Math.abs(scaled) * TIE_TOLERANCE_RATIO);

  if (distanceFromTie <= tolerance) {
    return quantizeTieOnDecimal(amount);
  }

  return boundCents(scaled);
}

/**
 * Converts a dollar amount to whole cents.
 *
 * Non-finite input yields 0, and so does an amount so large that shifting it
 * overflows — a finite input never produces a non-finite count, which would
 * otherwise win every comparison downstream and spread through arithmetic.
 * Amounts below that overflow are converted exactly, including ones past the
 * `numeric(15,2)` ceiling: substituting a bounded figure for them would be the
 * silent corruption {@link assertMoneyAmount} rejects loudly instead.
 */
export function toCents(amount: number): number {
  if (!Number.isFinite(amount)) {
    return 0;
  }
  return quantizeToCents(amount);
}

/**
 * Rounds a dollar amount to the nearest cent, discarding sub-cent float drift.
 * Non-finite input yields 0.
 *
 * The coercion keeps display and comparison paths total. A boundary that
 * produces a figure someone is billed must not rely on it — see
 * {@link assertMoneyAmount}.
 */
export function roundToCents(amount: number): number {
  return toCents(amount) / CENTS_PER_UNIT;
}

/**
 * Largest amount that still holds as an exact cent count.
 *
 * The ceiling matches the `numeric(15,2)` money columns. {@link
 * assertMoneyAmount} throws past it, because a settlement boundary must not
 * continue with a figure the database cannot express; the parse boundary in
 * `@/lib/money-input` answers `NaN` from this same value, so both agree on
 * which amounts exist without either restating the bound.
 */
export const MAX_SETTLEABLE_AMOUNT = MAX_PERSISTED_CENTS / CENTS_PER_UNIT;

/**
 * Returns `amount` when it is a settleable money figure, and throws otherwise.
 *
 * {@link roundToCents} coerces what it cannot express to 0 so rendering a
 * corrupt row does not crash a page. Settlement is different: coercing there
 * turns a `NaN` balance into a well-formed `$0.00` owed and hands the whole
 * payment back as an overpayment credit — a confident financial figure no
 * downstream consumer can tell apart from a real one.
 *
 * Two things are rejected, both for that same reason: non-finite input, and an
 * amount too large for the `numeric(15,2)` money columns. The second cannot
 * come from a stored row — only from client arithmetic or pasted input that
 * has already gone wrong.
 */
export function assertMoneyAmount(amount: number, label: string): number {
  if (!Number.isFinite(amount)) {
    throw new RangeError(`${label} must be a finite amount, received ${amount}`);
  }
  if (Math.abs(amount) > MAX_SETTLEABLE_AMOUNT) {
    throw new RangeError(`${label} is too large to settle to the cent, received ${amount}`);
  }
  return amount;
}

/**
 * Whether `amount` exceeds `limit` by at least a whole cent.
 *
 * Settlement guards compare a two-decimal operator entry (or a cent-normalized
 * seed the app wrote itself) against a balance this client re-derived in
 * floating point. A bare `>` therefore refuses the exact figure the UI renders:
 * a $10.00 credit with $1.00 and $1.06 applied displays `$7.94` and rejects
 * `7.94`, because the subtraction lands on 7.9399999999999995. Currency columns
 * are `numeric`, so the server's balance is exactly 7.94 and the drift is a
 * client-side artifact — comparing in cents restores agreement with the server
 * rather than loosening the guard.
 */
export function exceedsCents(amount: number, limit: number): boolean {
  return toCents(amount) > toCents(limit);
}

/**
 * Whether `amount` is at least one whole cent.
 *
 * A cent is the smallest applicable unit in this domain, so a sub-cent residual
 * left by float drift is not a spendable balance. `> 0` keeps a fully consumed
 * credit selectable as an un-appliable `$0.00` row; this predicate retires it.
 */
export function isPositiveCents(amount: number): boolean {
  return toCents(amount) > 0;
}
